import { useMemo, useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import { CalendarHeader } from './CalendarHeader';
import { ModesManager } from './modes/Manager';
import { CALENDAR_MODE__MONTH } from '../../../../constants';
import { getInterviewEvents, InterviewEvent } from '../../../../network/ats';
import { Event } from '../../../../types';

/** Shapes an interview as a calendar event for the shared calendar views. */
export function toCalendarEvent(interview: InterviewEvent): Event {
  const start = moment(`${interview.date}T${interview.time || '00:00'}`);
  const end = start.clone().add(Number(interview.duration) || 60, 'minutes');
  return {
    id: interview.id,
    type: 'Interview',
    title: `${interview.candidate?.name ?? 'Candidate'} (${interview.type})`,
    description: interview.notes ?? '',
    start: { date: start.format('YYYY-MM-DDTHH:mm'), time: 'none' },
    end: { date: end.format('YYYY-MM-DDTHH:mm'), time: 'none' },
    startTimeOfDay: start.format('HH:mm'),
    endTimeOfDay: end.format('HH:mm'),
    userFirstName: interview.interviewer?.name ?? '',
    userLastName: '',
    userFullName: `${interview.candidate?.name ?? 'Candidate'} with ${
      interview.interviewer?.name ?? 'interviewer'
    }`,
    userPictureURL: '',
  };
}

/** The ATS calendar: scheduled interviews, not leave. */
export function CalendarRoute() {
  const [mode, setMode] = useState(CALENDAR_MODE__MONTH);
  const [today, setToday] = useState(moment());

  // The visible range plus a week either side (month grids show edge weeks).
  const range = useMemo(
    () => ({
      start: today.clone().startOf('month').subtract(7, 'days').format('YYYY-MM-DD'),
      end: today.clone().endOf('month').add(7, 'days').format('YYYY-MM-DD'),
    }),
    [today],
  );

  const { data: interviews = [], isFetched, isError } = useQuery({
    queryKey: ['ats-interviews', range.start, range.end],
    queryFn: () => getInterviewEvents(range.start, range.end),
    keepPreviousData: true,
  });

  const events = useMemo(() => interviews.map(toCalendarEvent), [interviews]);

  const step = (direction: 1 | -1) =>
    setToday((prev) =>
      prev.clone().add(direction, mode as moment.unitOfTime.DurationConstructor),
    );

  return (
    <div className="flex overflow-hidden rounded-lg bg-white shadow lg:flex-1">
      <div className="flex-1 lg:flex lg:flex-col">
        <CalendarHeader
          today={today}
          mode={mode}
          setMode={setMode}
          onPrevious={() => step(-1)}
          onToday={() => setToday(moment())}
          onNext={() => step(1)}
        />
        {isError && (
          <p role="alert" className="px-8 py-2 text-sm text-red-600">
            Interviews could not be loaded. Please refresh the page.
          </p>
        )}
        {isFetched && <ModesManager mode={mode} events={events} today={today} />}
      </div>
    </div>
  );
}
